/** @format */
import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useParams } from 'react-router-dom'
import MonitorCrumb from 'src/components/MonitorCrumb'
import {
  _$,
  Card,
  BreadcrumbItem,
  Dialog,
  Message,
  Button,
  Icon,
  Select,
  Option
} from '@king-design/react'
import {
  GroupProductProps,
  FilterRelationMenu,
  FilterNameEnum,
  FilterFunctionEnum
} from 'src/containers/MonitorGroup/constants'
import ApiGroup from 'src/api/ApiGroup'
import MonitorSearch from 'src/components/MonitorSearch'
import useTableGroup from 'src/hooks/useTableGroup'
import { TablePage } from 'src/components'
import useFormTable from 'src/hooks/useFormTable'
import variable from 'src/static/styles/var'
import { request } from 'src/utils'
import { QueryKeyEnum } from 'src/utils/unit'
import { ProductItem, InstanceItem } from 'src/utils/interface'
// eslint-disable-next-line max-len
import CloudListTableCopyTips from 'src/containers/MonitorCloud/CloudListTable/CloudListTableCopyTips'
import EditGroupDrawer from 'src/containers/MonitorGroup/EditGroupDrawer'
import Cookie from 'js-cookie'

const lang = Cookie.get('ksc_lang')
const MonitorGroupDetails: React.FC<{ className: string }> = ({ className }) => {
  const { groupId, groupName } = useParams<{ groupId: string; groupName: string }>()
  const [productList, setProductList] = useState<ProductItem[]>([])
  const [filterProduct, setFilterProduct] = useState<ProductItem[]>([])
  const [groupProductList, setGroupProductList] = useState<GroupProductProps[]>()
  const [curProduct, setCurProduct] = useState<GroupProductProps>({} as GroupProductProps)
  // 自选实例 - 搜索
  const [queryKey, setQueryKey] = useState<string>('InstanceName')
  const [query, setQuery] = useState<string>('')
  const regionList = useTableGroup('monitorRegionList')
  const [tableGroup, setTableGroup] = useState({ childType: '', region: '' })
  const [tableCheckes, setTableChecks] = useState<string[]>()
  const [tableInfo, setTableInfo] = useState<boolean>(false)
  const [tableData, setTableData] = useState<InstanceItem[]>([])
  const [tableTotal, setTableTotal] = useState<number>()
  // 新建、修改产品线
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false)
  const [drawerType, setDrawerType] = useState<'edit' | 'create'>('create')

  // 获取所有产品线
  const getProductTypeList = async () => {
    const result = await request(ApiGroup.getProductList, {
      data: {
        TopTypeFlag: 1
      }
    })
    if (result.code === 'Success') {
      // 容器引擎-pod(29) -> 容器引擎-workload(45)
      const findIndex = result?.data?.productList.findIndex(item => item.productType === 31)
      result?.data?.productList[findIndex].childTypes.forEach(item => {
        if (item.productType === 29) {
          item.productType = 45
        }
      })
      setProductList(result.data.productList)
    }
  }

  useEffect(() => {
    getProductTypeList()
  }, [groupId])

  // 获取资源分组产品列表
  useEffect(() => {
    if (groupId && productList?.length) {
      getGroupProduct()
    }
  }, [groupId, productList])

  // 获取资源分组下已关联产品线
  const getGroupProduct = async (productType?: number) => {
    const result = await request(ApiGroup.getGroupProducts, {
      data: {
        GroupId: groupId
      }
    })
    if (result.code === 'Success') {
      setGroupProductList(result.data.data)
      let initProduct = null
      if (productType !== undefined) {
        initProduct =
          result.data.data.find(item => item.productType === productType) || result?.data?.data[0]
      } else {
        initProduct = result?.data?.data[0]
      }
      if (initProduct) {
        const find = productList.find(item => item.productType === initProduct.productType)
        if (find) {
          setCurProduct({
            ...initProduct,
            productName: find.productName,
            childTypes: find.childTypes
          })
        } else {
          setCurProduct({} as GroupProductProps)
        }
      } else {
        setCurProduct({} as GroupProductProps)
      }
    }
  }

  // 获取产品线下已绑定的资源
  const { run, result, total, pageState, loading } = useFormTable(
    ApiGroup.getGroupProductInstances,
    {
      manual: true,
      params: {
        GroupId: groupId,
        ProductType: tableGroup.childType || curProduct.productType,
        PageIndex: 1,
        PageSize: 20,
        InstanceId: '',
        InstanceIP: '',
        InstanceName: '',
        [queryKey]: query,
        Region: tableGroup.region
      }
    },
    data => {
      const properties = data?.resourceList?.find(item => {
        return item?.properties?.length
      })
      setTableInfo(!!properties)
      return {
        tableData: data?.resourceList || [],
        totalCount: data?.totalCount || 0
      }
    }
  )

  useEffect(() => {
    setTableData(result || [])
    setTableTotal(total || 0)
  }, [result, total])

  // 过滤已有产品线
  useEffect(() => {
    if (productList?.length && groupProductList) {
      const filter = productList.filter(({ productType }) => {
        return !groupProductList.find(item => item.productType === productType)
      })
      setFilterProduct(filter as ProductItem[])
    }
  }, [groupProductList, productList])

  // 删除产品线
  const delProduct = async (e, productType) => {
    e.stopPropagation()
    Dialog.confirm({
      content: _$('删除后产品线下所有资源将自动解除，确认删除该产品线')
    }).then(async () => {
      const result = await request(ApiGroup.removeGroupProduct, {
        method: 'POST',
        data: {
          GroupId: parseInt(groupId),
          ProductType: productType
        }
      })
      if (result.code === 'Success') {
        getGroupProduct()
        Message.success(_$('移除成功'))
      }
    })
  }

  // table scheme
  const tableScheme = useMemo(() => {
    if (regionList?.length) {
      const typeGroup = [
        {
          label: _$('全部'),
          value: ''
        }
      ]
      if (curProduct?.childTypes?.length) {
        curProduct.childTypes.forEach(({ productType, productName }) => {
          typeGroup.push({
            label: _$(productName),
            value: _$(productType)
          })
        })
      }
      const scheme = {
        instanceName: {
          title: _$('实例名称'),
          template: item => {
            return (
              <div className="table-name">
                <span>{item.instanceName}</span>
                <CloudListTableCopyTips instanceId={item.instanceId} margin={true} />
              </div>
            )
          }
        },
        instanceIP: _$('实例信息'),
        childType: {
          title: _$('type'),
          group: typeGroup,
          template: item => {
            const find = curProduct?.childTypes?.find(child => child.productType === item.childType)
            return find ? find.productName : ''
          }
        },
        region: {
          title: _$('region'),
          group: regionList,
          template(item) {
            const find = regionList.find(region => region.value === item.region)
            return find?.label && lang !== 'en' ? find?.label : item.region
          }
        },
        properties: {
          title: _$('备注'),
          minWidth: 200,
          template({ properties }) {
            return properties?.length
              ? properties.map(({ name, value }) =>
                  name && value ? (
                    <p style={{ margin: 0 }}>
                      {name}：{value}
                    </p>
                  ) : (
                    ''
                  )
                )
              : ''
          }
        },
        action: {
          title: _$('操作'),
          width: 120,
          template(item) {
            return (
              <a
                onClick={() => {
                  batchDelete(false, [item.instanceId])
                }}
              >
                {_$('移除')}
              </a>
            )
          }
        }
      }
      if (!curProduct?.childTypes?.length) {
        delete scheme.childType
      }
      if (curProduct?.groupDynamicRules) {
        delete scheme.action
      }
      if (!tableInfo) {
        delete scheme.properties
      }
      return scheme
    }
    return undefined
  }, [curProduct, regionList, tableInfo])

  // 切换产品线
  useEffect(() => {
    resetValues()
    if (curProduct?.productType !== undefined) {
      run({
        ProductType: curProduct.productType,
        PageIndex: 1,
        InstanceId: '',
        InstanceIP: '',
        InstanceName: '',
        Region: ''
      })
    } else {
      setTableData([])
      setTableTotal(0)
    }
  }, [curProduct])

  // 重置所有筛选条件
  const resetValues = () => {
    setTableChecks([])
    setQueryKey('InstanceName')
    setQuery('')
    setTableGroup({
      region: '',
      childType: ''
    })
  }

  // 自选实例 - 批量删除
  const batchDelete = (flag: boolean, ids = tableCheckes) => {
    Dialog.confirm({
      content: flag ? _$(`确认批量移除资源？`) : _$('确认移除当前资源？')
    }).then(async () => {
      if (ids?.length) {
        const result = await request(ApiGroup.removeGroupProductInstance, {
          method: 'POST',
          data: {
            GroupId: parseInt(groupId),
            Instances: ids.map(item => ({
              InstanceId: item,
              ProductType: curProduct.productType
            }))
          }
        })
        if (result.code === 'Success') {
          getGroupProduct(curProduct.productType)
          Message.success(_$('移除成功'))
        }
      }
    })
  }

  // 打开抽屉
  const openDrawer = (type: 'create' | 'edit') => {
    setDrawerType(type)
    setDrawerVisible(true)
  }

  return (
    <div className={className}>
      <MonitorCrumb breadArr={[{ title: _$('资源分组'), route: '/monitor/group/home' }]}>
        {groupName ? <BreadcrumbItem>{groupName}</BreadcrumbItem> : null}
      </MonitorCrumb>
      <Card>
        <div className="header">
          <Button
            type="primary"
            onClick={() => {
              openDrawer('create')
            }}
          >
            <Icon className="ion-plus-round" />
            {_$('新建产品线')}
          </Button>
        </div>
        {groupProductList?.length ? (
          <>
            {curProduct?.productType !== undefined && groupProductList?.length ? (
              <div className="product-tabs">
                {groupProductList.map(item => {
                  const find = productList.find(product => product.productType === item.productType)
                  const { productType, productName, childTypes = [] } = find || {}
                  return find ? (
                    <div
                      className={curProduct.productType === productType ? 'selected' : ''}
                      key={productType}
                      onClick={() => {
                        if (item.productType !== curProduct.productType) {
                          setCurProduct({
                            ...item,
                            productName,
                            childTypes
                          })
                        }
                      }}
                    >
                      <p className="name">{productName}</p>
                      <p className="total">({item.resourceNumber})</p>
                      <Icon
                        onClick={e => {
                          delProduct(e, productType)
                        }}
                        className="ion-close-round"
                      />
                    </div>
                  ) : null
                })}
              </div>
            ) : null}
            {curProduct?.productType !== undefined && !!curProduct.groupDynamicRules ? (
              <div className="match-condition">
                <p>{_$('匹配条件')}：</p>
                {curProduct?.groupDynamicRules?.filters?.map((item, index) => (
                  <p
                    className="filter-text"
                    // eslint-disable-next-line max-len
                    key={`${curProduct.productType}-${item.name}-${item.function}-${item.value}-${index}`}
                  >
                    {_$(FilterNameEnum[item.name])}
                    {_$(FilterFunctionEnum[item.function])}
                    {item.value}
                  </p>
                ))}
                <p> {_$(FilterRelationMenu[curProduct.groupDynamicRules?.relation])}</p>
              </div>
            ) : null}
            <div className="table-header">
              {curProduct?.productType !== undefined && !curProduct.groupDynamicRules ? (
                <div className="table-btns">
                  <Button
                    type="primary"
                    onClick={() => {
                      openDrawer('edit')
                    }}
                  >
                    <Icon className="ion-plus-round" />
                    {_$('新建实例')}
                  </Button>
                  <Button
                    disabled={tableCheckes?.length < 1}
                    onClick={() => {
                      batchDelete(true)
                    }}
                  >
                    <Icon className="ion-trash-a" />
                    {_$('批量移除')}
                  </Button>
                </div>
              ) : null}
              {curProduct?.productType !== undefined && !!curProduct.groupDynamicRules ? (
                <Button
                  type="primary"
                  onClick={() => {
                    openDrawer('edit')
                  }}
                >
                  <Icon className="ion-edit" style={{ fontSize: '12px' }} />
                  {_$('编辑条件')}
                </Button>
              ) : null}
              <MonitorSearch
                prepend={
                  <React.Fragment>
                    <Select
                      value={queryKey}
                      onChangeValue={value => {
                        setQueryKey(value)
                        if (query) {
                          run({
                            PageIndex: 1,
                            InstanceId: '',
                            InstanceName: '',
                            InstanceIP: '',
                            [value]: query
                          })
                        }
                      }}
                      width="150"
                    >
                      <Option value="InstanceName">{_$('实例名称')}</Option>
                      <Option value="InstanceId">{_$('实例Id')}</Option>
                      <Option value="InstanceIP">{_$('实例信息')}</Option>
                    </Select>
                  </React.Fragment>
                }
                width={400}
                placeholder={_$(`请输入${QueryKeyEnum[queryKey]}`)}
                value={query}
                onChange={value => {
                  setQuery(value.trim())
                  run({
                    PageIndex: 1,
                    InstanceId: '',
                    InstanceName: '',
                    InstanceIP: '',
                    [queryKey]: value.trim()
                  })
                }}
              />
            </div>
          </>
        ) : null}
        {tableScheme ? (
          <TablePage
            tableProps={{
              rowKey: val => val.instanceId,
              data: tableData,
              scheme: tableScheme,
              checkType: !!curProduct?.groupDynamicRules ? 'none' : 'checkbox',
              rowCheckable: false,
              checkedKeys: tableCheckes,
              onChangeCheckedKeys: value => {
                setTableChecks(value)
              },
              group: tableGroup,
              onChangeGroup: value => {
                if (
                  value.region !== tableGroup.region ||
                  value.childType !== tableGroup.childType
                ) {
                  setTableGroup(value)
                  run({
                    ProductType: value.childType || curProduct.productType,
                    Region: value.region,
                    PageIndex: 1
                  })
                }
              }
            }}
            pageProps={{ total: tableTotal, ...pageState }}
            loading={loading}
            run={run}
          />
        ) : null}
      </Card>
      {regionList?.length ? (
        <EditGroupDrawer
          visible={drawerVisible}
          groupId={groupId}
          defaultData={drawerType === 'edit' ? curProduct : undefined}
          drawerType={drawerType}
          productList={filterProduct}
          onClose={(updata, productType) => {
            setDrawerVisible(false)
            updata && getGroupProduct(productType)
          }}
        />
      ) : null}
    </div>
  )
}

export default styled(MonitorGroupDetails)`
  padding: 0 16px;
  .product-tabs {
    margin: 8px 0 16px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;
    & > div {
      margin: 8px 8px 0 0;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0 16px;
      font-size: 12px;
      line-height: 30px;
      background: #eee;
      cursor: pointer;
      &.selected {
        background: ${variable.primaryColor};
        color: #fff;
        .ion-close-round {
          color: #fff;
        }
      }
      &:hover {
        padding-right: 7px;
        .ion-close-round {
          display: block;
        }
      }
      p {
        margin-bottom: 0;
        margin-right: 4px;
      }
      .ion-close-round {
        font-size: 12px;
        cursor: pointer;
        display: none;
      }
    }
  }
  .match-condition {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-left: 8px;
    padding-bottom: 8px;
    p {
      margin-bottom: 4px;
    }
    .filter-text {
      padding: 6px 10px;
      background: #eee;
      margin-right: 8px;
    }
  }
  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    .table-btns {
      .k-btn {
        margin-right: 8px;
      }
    }
  }
  .k-table {
    .table-name {
      display: inline-block;
      span {
        margin-bottom: 0;
        margin-right: 8px;
        word-break: break-all;
      }
    }
  }
`
